// Library methods
import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";

// Utilities
import { updateExam } from "../../services/Exam";

import { examCardEnums, eyeTypes } from "../../enums";
import useClinic from "../useClinic";
import { ToastContext } from "../../contexts/ToastContext";
import { useAddEditExamHelpers } from "./useAddEditExamHelpers";

export const useEditExams = () => {
  const { clinicId } = useClinic();
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const { setToast } = useContext(ToastContext);

  const { getVfEyeSection, getEyeSection, getSectionName, isVfOrFull120 } =
    useAddEditExamHelpers();

  const { eyeLeft, eyeRight, eyeBoth } = eyeTypes;

  const hasMonocularSections = useCallback(
    ({ selectedExam }) => {
      const { examFieldType, binocular } = selectedExam;
      return (
        isVfOrFull120(selectedExam) ||
        (!binocular &&
          (examFieldType === examCardEnums.COLOR_VISION.algorithmPath ||
            examFieldType ===
              examCardEnums.CONTRAST_SENSITIVITY.algorithmPath ||
            examFieldType === examCardEnums.VISUAL_ACUITY.algorithmPath))
      );
    },
    [isVfOrFull120]
  );

  const getMonocularSections = useCallback(
    ({ selectedExam }) => {
      const { eyeCheckboxes } = selectedExam;

      // array for left and right eye data
      const monocularSections = [];

      if (eyeCheckboxes.right) {
        const eyeSection = isVfOrFull120(selectedExam)
          ? getVfEyeSection(eyeRight, selectedExam)
          : getEyeSection(eyeRight, selectedExam);
        monocularSections.push(eyeSection);
      }
      if (eyeCheckboxes.left) {
        const eyeSection = isVfOrFull120(selectedExam)
          ? getVfEyeSection(eyeLeft, selectedExam)
          : getEyeSection(eyeLeft, selectedExam);
        monocularSections.push(eyeSection);
      }

      return monocularSections;
    },
    [isVfOrFull120, getVfEyeSection, getEyeSection, eyeLeft, eyeRight]
  );

  const editExamData = useCallback(
    async ({
      sectionTypeOrder,
      scheduledDate,
      examSections,
      patientId,
      editExamId,
    }) => {
      const examData = {
        sectionTypeOrder: sectionTypeOrder.join(", "),
        scheduledDate: scheduledDate.toUTC().toISO(),
        ...examSections,
      };

      // send new data to api
      try {
        const token = await getAccessTokenSilently();
        await updateExam(token, clinicId, patientId, editExamId, examData);
      } catch (error) {
        setToast(() => ({
          success: true,
          message: t("exams_edit_error"),
        }));
      }
    },
    [clinicId, setToast, getAccessTokenSilently, t]
  );

  const getFormattedExamSections = useCallback(
    ({ selectedExamCards }) => {
      let examSections = {};

      // loop over the exam sections
      selectedExamCards.forEach((selectedExam) => {
        const { examFieldType } = selectedExam;
        const sectionName = getSectionName(selectedExam);

        // For Visual Field exams (except Esterman) and other monocular exam types
        if (hasMonocularSections({ selectedExam })) {
          const monocularSections = getMonocularSections({
            selectedExam,
          });
          examSections[sectionName] = monocularSections;
        }
        // For Esterman exams
        else if (
          examFieldType === examCardEnums.VISUAL_FIELD_ESTERMAN.algorithmPath
        ) {
          const estermanSection = getVfEyeSection(eyeBoth, selectedExam);
          examSections[sectionName] = [estermanSection];
        }
        // For binocular exams
        // (Contrast Sensitivity, Color Vision, Visual Acuity, Synoptophore, RAPD)
        else {
          const binocularSection = getEyeSection(eyeBoth, selectedExam);
          examSections[sectionName] = [binocularSection];
        }
      });

      return examSections;
    },
    [
      hasMonocularSections,
      getSectionName,
      getVfEyeSection,
      getMonocularSections,
      getEyeSection,
      eyeBoth,
    ]
  );

  // edit an exam
  const editExam = useCallback(
    async ({
      selectedExamCards, // updated exam data
      sectionTypeOrder,
      scheduledDate,
      patientId,
      editExamId,
      onEditExamCallback,
    }) => {
      const examSections = getFormattedExamSections({ selectedExamCards });
      // edit exam data
      await editExamData({
        sectionTypeOrder,
        scheduledDate,
        examSections,
        patientId,
        editExamId,
      });
      await onEditExamCallback(editExamId);
    },
    [editExamData, getFormattedExamSections]
  );

  return {
    editExam,
    getFormattedExamSections,
  };
};
